import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useEffect, useState, useRef } from 'react'
import { useTranslate } from '@repo/unicity'
import { memberCalls2Url } from '@helpers'
import SuccessIcon from '@/core/components/payment.response/assets/SuccessIcon.svg?react'
import FailedIcon from '@/core/components/payment.response/assets/FailedIcon.svg?react'
import TicketLayout, { type TicketData } from '@/core/components/payment.response/TicketLayout'

const SuccessPage = () => {
    const navigate = useNavigate()
    const { translate } = useTranslate()
    const { responseCode } = useParams<{ responseCode: string }>()
    const [searchParams] = useSearchParams()

    const [ticketData, setTicketData] = useState<TicketData | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const hasFetched = useRef(false)

    useEffect(() => {
        // Prevent double fetch (React StrictMode)
        if (hasFetched.current) return
        hasFetched.current = true

        const fetchTicketData = async () => {
            // Get refId from query string (for failed redirect) or from URL params (for success)
            const refId = searchParams.get('refId')
            const code = refId || responseCode || ''

            if (!code) {
                setError('No reference code provided')
                setIsLoading(false)
                return
            }

            try {
                setIsLoading(true)
                const url = memberCalls2Url({ path: `/glic-events/2027/ticket/${code}` })
                const response = await fetch(url)
                const result = await response.json()

                if (result.success && result.data) {
                    setTicketData(result.data)
                } else {
                    setError('Failed to load ticket data')
                }
            } catch (err) {
                console.error('Error fetching ticket:', err)
                setError('Failed to load ticket data')
            } finally {
                setIsLoading(false)
            }
        }

        fetchTicketData()
    }, [responseCode, searchParams])

    useEffect(() => {
        // Prevent going back to payment
        window.history.pushState(null, '', window.location.href)

        const handlePopState = () => {
            navigate('/', { replace: true })
        }

        window.addEventListener('popstate', handlePopState)
        return () => window.removeEventListener('popstate', handlePopState)
    }, [navigate])

    const isPaymentSuccess = ticketData?.payment_status === 'success'

    return (
        <Stack
            sx={{
                flex: 1,
                padding: '30px 20px',
                alignItems: 'center',
                justifyContent: 'flex-start',
                textAlign: 'center',
                gap: 3,
                background: 'rgba(229, 235, 240, 1)'
            }}>
            {/* Success/Fail Icon */}
            <Box>{isLoading ? null : isPaymentSuccess ? <SuccessIcon /> : <FailedIcon />}</Box>

            {error ? (
                <Typography color="error">{error}</Typography>
            ) : (
                <TicketLayout
                    title={isPaymentSuccess ? translate('payment_sucessful') : translate('transaction_failed')}
                    description={undefined}
                    data={ticketData || undefined}
                    isLoading={isLoading}
                    isSuccess={isPaymentSuccess}
                />
            )}
        </Stack>
    )
}

export default SuccessPage
